import React, { useRef, useState } from 'react';
import { Form, Input, Layout, LayoutItem, Loader } from '@axiom/ui';

import type { ChatHistoryItem } from '../../api/chat-api';

import { ChatHistoryRenameFormSchema } from './chat-history-rename-form-schema';

export type ChatHistoryRenameProps = {
  chat: ChatHistoryItem;
  renameChat: ({
    chatId,
    name,
  }: {
    chatId: ChatHistoryItem['id'];
    name: string;
  }) => Promise<boolean>;
  onClose: () => void;
};

/**
 * The rename field, in place of the row it belongs to.
 *
 * Its own `Form`: `RawForm` renders a real `<form>` and a form cannot contain another, so
 * this cannot sit inside the composer's form - `Spark` keeps that form around the centre
 * column only.
 */
export const ChatHistoryRename = ({
  chat,
  renameChat,
  onClose,
}: ChatHistoryRenameProps) => {
  /*
    The last name actually sent. Compared against this rather than `chat.name`, which is the
    name the *list* holds and does not change until the response lands - so a second commit
    inside that window would look like a fresh edit and take a server lock for nothing.
  */
  const lastSentRef = useRef(chat.name ?? '');

  /*
    Whether the field was abandoned rather than left. Set by Escape, read by the blur that
    unmounting the input produces.
  */
  const cancelledRef = useRef(false);

  /*
    Whether a write is in flight. State rather than a ref because it is *rendered* - the field
    goes read-only and a spinner appears - and it is what holds the field open across the write,
    where closing on submit leaves the row showing the old name for the length of the round trip.
  */
  const [saving, setSaving] = useState(false);

  /*
    The name as it stood when the field opened, captured **once**. `Form` hardcodes Formik's
    `enableReinitialize` (`Form.tsx:102`), which calls `resetForm()` whenever `initialValues`
    stops being deep-equal, so computing it from the `chat` prop lets a save that lands while
    the field is open reset it and discard whatever was typed in flight.
  */
  const initialValuesRef = useRef({ name: chat.name ?? '' });

  return (
    <Form
      name="RENAME_CHAT_FORM"
      schema={ChatHistoryRenameFormSchema}
      initialValues={initialValuesRef.current}
      onSubmit={async ({ name }) => {
        /*
          A falsy name is a no-op rather than an error, and `.trim()` in the schema is what
          makes a whitespace-only entry arrive here as `''`. An unchanged name is skipped for
          the same reason: opening the field and leaving it alone should not take a server lock.
        */
        if (!name || name === lastSentRef.current) {
          onClose();

          return;
        }

        lastSentRef.current = name;
        setSaving(true);

        const saved = await renameChat({ chatId: chat.id, name });

        if (!saved) {
          setSaving(false);
          lastSentRef.current = chat.name ?? '';

          return;
        }

        onClose();
      }}
    >
      {({ fireSubmit }) => {
        const commit = () => {
          if (saving) return;

          void fireSubmit();
        };

        const cancel = () => {
          if (saving) return;

          cancelledRef.current = true;
          onClose();
        };

        return (
          <div
            onKeyDown={event => {
              if (event.key !== 'Enter' && event.key !== 'Escape') return;

              /*
                Enter would otherwise also submit the form natively - one text field, nothing
                blocking implicit submission - which is a second write of the same value.
                Escape is stopped because it is a page-level gesture elsewhere, closing a
                drawer or a modal.
              */
              event.preventDefault();
              event.stopPropagation();

              if (event.key === 'Escape') {
                cancel();

                return;
              }

              commit();
            }}
          >
            <Layout horizontalGutter="8px" position="middle">
              <LayoutItem fluid>
                <Input
                  disabled={saving}
                  name="name"
                  onBlur={() => {
                    if (cancelledRef.current) return;

                    commit();
                  }}
                  placeholder="Conversation name"
                />
              </LayoutItem>
              {saving ? <Loader name="RENAME_SAVING" type="dots" /> : null}
            </Layout>
          </div>
        );
      }}
    </Form>
  );
};
